import { AppMode } from 'app/features/settings/models';
import { Activity, ActivityType } from 'app/shared/features/activity-selector/models';
import { isLocationTypeAndResultDeterminationTypeSupported } from 'app/shared/features/activity-selector/utils';
import { LocationType } from 'app/shared/features/location-selector/models';
import { ResultDeterminationType } from 'app/shared/models';
import { MeasurementType } from 'app/shared/models/measurement-type';

export function isActivitySupportedForScanFlow(a: Activity) {
    return (
        [
            ActivityType.Break,
            ActivityType.Harvest,
            ActivityType.Generic,
            ActivityType.CropMaintenance,
            ActivityType.Packing,
        ].includes(a.activityType) && isLocationTypeAndResultDeterminationTypeSupported(a)
    );
}

export const isEmployeeRequired = (appMode: AppMode) => appMode === 'multi-user';

export function isBatchRequired(activity: Activity | undefined) {
    if (!activity) return false;
    return activity.isBatchInputRequired;
}

// All location types except Row and null are supported for single location selection only
export function isSingleLocationRequired(activity: Activity | undefined) {
    if (!activity) return false;
    return activity.locationType !== LocationType.Row && !isLocationTypeNullOrSite(activity);
}

export function isOneOrManyLocationRequired(activity: Activity | undefined) {
    if (!activity) return false;
    return activity.locationType === LocationType.Row;
}
export function isLocationTypeNullOrSite(activity: Activity | undefined) {
    if (!activity) return false;
    return activity.locationType === LocationType.Site || activity.locationType === null;
}
export function isSingleContainerRequired(activity: Activity | undefined) {
    if (!activity) return false;
    return activity.resultDeterminationType === ResultDeterminationType.byOneContainer;
}

export function isSingleAmountResultRequired(activity: Activity | undefined) {
    if (!activity) return false;
    return activity.resultDeterminationType === ResultDeterminationType.byAmount;
}

export function isOneOrManyContainersRequired(activity: Activity | undefined) {
    if (!activity) return false;
    return [ResultDeterminationType.byContainer, ResultDeterminationType.ByContainerAndProductBox].includes(
        activity.resultDeterminationType,
    );
}

export function isOneOrManyProductBoxRegistrationsRequired(activity: Activity | undefined) {
    if (!activity) return false;
    return activity.resultDeterminationType === ResultDeterminationType.byAmountAndProductBox;
}

export function isProductBoxListRequired(activity: Activity | undefined) {
    if (!activity) return false;
    return activity.resultDeterminationType === ResultDeterminationType.ByContainerAndProductBox;
}

export function isContainerMeasurementWeight(activity: Activity | undefined) {
    if (!activity) return false;
    return activity.measurementType === MeasurementType.containerWeight;
}

export function isContainerMeasurementProductBoxes(activity: Activity | undefined) {
    if (!activity) return false;
    return activity.measurementType === MeasurementType.containerProductBoxes;
}

export function isPackingActivity(activity: Activity | undefined) {
    if (!activity) return false;
    return (
        activity.activityType === ActivityType.Packing &&
        activity.measurementType !== MeasurementType.noMeasurementType
    );
}

export function isActivityMatchLocationType(
    currentActivity: Activity | undefined,
    scannedCodeLocationType: LocationType | null,
) {
    return currentActivity?.locationType === scannedCodeLocationType;
}

export function isRequiredActivityEntityEqual(newActivity: Activity, currentActivity: Activity | undefined) {
    if (currentActivity === undefined) return false;
    return (
        newActivity.name === currentActivity.name &&
        newActivity.resultDeterminationType === currentActivity.resultDeterminationType &&
        newActivity.locationType === currentActivity.locationType &&
        newActivity.activityType === currentActivity.activityType &&
        newActivity.nrOfDecimalsAfterDecimalPoint === currentActivity.nrOfDecimalsAfterDecimalPoint &&
        newActivity.nrOfDecimalsBeforeDecimalPoint === currentActivity.nrOfDecimalsBeforeDecimalPoint &&
        newActivity.presentationUnit === currentActivity.presentationUnit &&
        newActivity.orderedProductBoxNumbers.length === currentActivity.orderedProductBoxNumbers.length
    );
}

export function hasBatchPropertyOnly(activity: Activity) {
    return (
        isLocationTypeNullOrSite(activity) &&
        activity.resultDeterminationType === ResultDeterminationType.noResult
    );
}

export const isActivityWithProductBoxRequirement = (activity: Activity) =>
    isOneOrManyProductBoxRegistrationsRequired(activity) ||
    isProductBoxListRequired(activity) ||
    isContainerMeasurementProductBoxes(activity);
